import React, { useState, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  reviewsRef,
  getUserRef,
  transactionsRef,
  serverTimestamp,
} from '../../firebase/collections';
import { AuthContext } from '../../contextStore/AuthContext';
import { ToastContext } from '../../contextStore/ToastContext';
import { createNotification } from '../../firebase/notifications';
import ButtonSpinner from '../UI/ButtonSpinner';
import './Reviews.css';

function ReviewModal({ sellerId, productId, productName, onClose, onSuccess }) {
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);
  const { addToast } = useContext(ToastContext);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [ratings, setRatings] = useState({ communication: 0, accuracy: 0, punctuality: 0, fairness: 0 });
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [canReview, setCanReview] = useState(false);
  const [checkingEligibility, setCheckingEligibility] = useState(true);

  // Check that a completed transaction exists between this buyer and seller
  useEffect(() => {
    if (!user?.uid || !sellerId) {
      setCheckingEligibility(false);
      return;
    }
    transactionsRef()
      .where('buyerId', '==', user.uid)
      .where('sellerId', '==', sellerId)
      .where('status', '==', 'completed')
      .limit(1)
      .get()
      .then((snap) => {
        setCanReview(!snap.empty);
        setCheckingEligibility(false);
      })
      .catch(() => {
        setCanReview(false);
        setCheckingEligibility(false);
      });
  }, [user?.uid, sellerId]);

  if (checkingEligibility) {
    return (
      <div className="reviewModalOverlay" onClick={onClose}>
        <div className="reviewModal" onClick={(e) => e.stopPropagation()}>
          <p>{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (!canReview) {
    return (
      <div className="reviewModalOverlay" onClick={onClose}>
        <div className="reviewModal" onClick={(e) => e.stopPropagation()}>
          <h3>{t('reviews.leaveReview')}</h3>
          <p className="reviewModalProduct">{t('reviews.mustCompleteDeal')}</p>
          <div className="reviewModalActions">
            <button type="button" onClick={onClose}>{t('common.close')}</button>
          </div>
        </div>
      </div>
    );
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!user?.uid || !sellerId || rating < 1 || rating > 5) {
      addToast(t('reviews.selectRating'), 'error');
      return;
    }
    setLoading(true);
    const reviewData = {
      sellerId,
      reviewerId: user.uid,
      reviewerName: user.displayName || user.email?.split('@')[0] || 'User',
      reviewerAvatar: user.photoURL || null,
      productId: productId || null,
      rating,
      ratings: (ratings.communication || ratings.accuracy || ratings.punctuality || ratings.fairness)
        ? { ...ratings }
        : null,
      comment: comment.trim() || null,
      sellerResponse: { text: '', respondedAt: null },
      helpfulCount: 0,
      reportCount: 0,
      verified: false,
      status: 'active',
      createdAt: serverTimestamp(),
    };
    reviewsRef()
      .add(reviewData)
      .then(() => {
        return getUserRef(sellerId)
          .get()
          .then((doc) => {
            const d = doc.data() || {};
            const sum = (d.ratingSum || 0) + rating;
            const count = (d.ratingCount || 0) + 1;
            return getUserRef(sellerId).update({
              ratingSum: sum,
              ratingCount: count,
            });
          });
      })
      .then(() => {
        createNotification(
          sellerId,
          'review',
          t('reviews.newReview'),
          t('reviews.starReview', { name: reviewData.reviewerName, rating: rating }),
          { productId }
        );
        addToast(t('reviews.reviewSubmitted'), 'success');
        onSuccess && onSuccess();
        onClose();
      })
      .catch((err) =>
        addToast(err.message || t('reviews.failedToSubmit'), 'error')
      )
      .finally(() => setLoading(false));
  };

  return (
    <div className="reviewModalOverlay" onClick={onClose}>
      <div className="reviewModal" onClick={(e) => e.stopPropagation()}>
        <h3>{t('reviews.leaveReview')}</h3>
        {productName && <p className="reviewModalProduct">{productName}</p>}
        <form onSubmit={handleSubmit}>
          <label>{t('reviews.rating')}</label>
          <div className="reviewStars">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                className="reviewStarBtn"
                onMouseEnter={() => setHoverRating(star)}
                onMouseLeave={() => setHoverRating(0)}
                onClick={() => setRating(star)}
              >
                <span
                  className={star <= (hoverRating || rating) ? 'filled' : ''}
                >
                  &#9733;
                </span>
              </button>
            ))}
          </div>
          <label className="reviewModalSubLabel">{t('reviews.detailedRatings')}</label>
          <div className="reviewModalDetails">
            {['communication', 'accuracy', 'punctuality', 'fairness'].map((key) => (
              <div key={key} className="reviewModalDetailRow">
                <span>{t(`reviews.${key.charAt(0).toUpperCase() + key.slice(1)}`)}</span>
                <div className="reviewStars reviewStarsSmall">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      className="reviewStarBtn"
                      onClick={() => setRatings((p) => ({ ...p, [key]: star }))}
                    >
                      <span className={star <= (ratings[key] || 0) ? 'filled' : ''}>&#9733;</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <label>{t('reviews.comment')}</label>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={t('reviews.shareExperience')}
            rows={4}
          />
          <div className="reviewModalActions">
            <button type="button" onClick={onClose}>
              {t('common.cancel')}
            </button>
            <button type="submit" disabled={loading}>
              {loading && <ButtonSpinner />}{t('common.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ReviewModal;
